// ── BURN RATE — Pricing engine ──────────────────────────────────────────────
// Pure calculation layer shared by the three calculator modes.
// Kept free of React so it can be reasoned about and tested in isolation.

export type CalcMode = "services" | "recipes" | "product";

// ── Country / currency presets ───────────────────────────────────────────────
// Full dataset lives in ./countries. Re-exported here so existing imports
// (COUNTRIES = featured chips, getCountry) keep working unchanged.

export type { CountryPreset } from "./countries";
export { FEATURED_COUNTRIES as COUNTRIES, WORLD_COUNTRIES, getCountry } from "./countries";

// ── Helpers ──────────────────────────────────────────────────────────────────

export const num = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const makeFmt =
  (locale: string) =>
  (v: number): string =>
    (Number.isFinite(v) ? v : 0).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

// ── Rounding (psychological pricing) ─────────────────────────────────────────

export type RoundingMode = "none" | "nearest5" | "nearest10" | "charm99" | "charm95";

export const ROUNDING_LABELS: Record<RoundingMode, string> = {
  none: "Exacto",
  nearest5: "Múltiplo de 5",
  nearest10: "Múltiplo de 10",
  charm99: "Terminar en .99",
  charm95: "Terminar en .95",
};

export function applyRounding(v: number, mode: RoundingMode): number {
  if (!Number.isFinite(v) || v <= 0) return 0;
  switch (mode) {
    case "nearest5":  return Math.ceil(v / 5) * 5;
    case "nearest10": return Math.ceil(v / 10) * 10;
    case "charm99":   return Math.max(Math.ceil(v) - 0.01, Math.floor(v) + 0.99);
    case "charm95":   return Math.floor(v) + 0.95 >= v ? Math.floor(v) + 0.95 : Math.ceil(v) + 0.95;
    default:          return v;
  }
}

// ── Shared pricing layer: cost → price → taxes → commissions ──────────────────

export interface PricingSettings {
  margin: number;          // profit margin %
  rounding: RoundingMode;
  // commissions (eat into revenue — grossed up so you still net your price)
  commissionsEnabled: boolean;
  paymentPct: number;      // card / payment processor %
  paymentFixed: number;    // fixed fee per transaction
  platformPct: number;     // delivery app / marketplace %
  // taxes
  ivaEnabled: boolean;
  ivaRate: number;
  // retentions (B2B services: client withholds part of your payment)
  retentionsEnabled: boolean;
  isrRet: number;
  ivaRet: number;
}

export interface PricingResult {
  baseCost: number;
  netPrice: number;        // price you need to net (cost + margin, rounded)
  markupPct: number;       // profit / cost
  marginPct: number;       // profit / price
  profit: number;
  commissionTotal: number;
  listedPrice: number;     // price to list so you still net after commissions
  ivaAmount: number;
  clientPays: number;      // listed + IVA — what the buyer actually pays
  retentionTotal: number;
  youReceive: number;      // after retentions (services invoicing companies)
}

export function computePricing(baseCost: number, s: PricingSettings): PricingResult {
  const safeCost = Number.isFinite(baseCost) && baseCost > 0 ? baseCost : 0;

  const priceRaw = s.margin < 100 && s.margin >= 0 ? safeCost / (1 - s.margin / 100) : 0;
  const netPrice = applyRounding(priceRaw, s.rounding);

  const profit = netPrice - safeCost;
  const markupPct = safeCost > 0 ? (profit / safeCost) * 100 : 0;
  const marginPct = netPrice > 0 ? (profit / netPrice) * 100 : 0;

  // Gross up for commissions so the listed price still nets `netPrice`
  const commPct = s.commissionsEnabled ? (s.paymentPct + s.platformPct) / 100 : 0;
  const fixed = s.commissionsEnabled ? s.paymentFixed : 0;
  const listedPrice = commPct < 1 ? (netPrice + fixed) / (1 - commPct) : netPrice;
  const commissionTotal = listedPrice - netPrice;

  const ivaAmount = s.ivaEnabled ? listedPrice * (s.ivaRate / 100) : 0;
  const clientPays = listedPrice + ivaAmount;

  const retentionTotal = s.retentionsEnabled
    ? listedPrice * (s.isrRet / 100) + listedPrice * (s.ivaRet / 100)
    : 0;
  const youReceive = clientPays - retentionTotal;

  return {
    baseCost: safeCost,
    netPrice,
    markupPct,
    marginPct,
    profit,
    commissionTotal,
    listedPrice,
    ivaAmount,
    clientPays,
    retentionTotal,
    youReceive,
  };
}

// ── Break-even ───────────────────────────────────────────────────────────────

export function breakevenUnits(fixedMonthly: number, pricePerUnit: number, variableCostPerUnit: number): number {
  const contribution = pricePerUnit - variableCostPerUnit;
  if (contribution <= 0 || fixedMonthly <= 0) return 0;
  return fixedMonthly / contribution;
}

// ── Food cost health (recipes) ───────────────────────────────────────────────
// Industry target food cost is ~28–35% of menu price.

export type FoodCostHealth = "good" | "warn" | "bad";

export function foodCostHealth(foodCostPct: number): FoodCostHealth {
  if (foodCostPct <= 0) return "good";
  if (foodCostPct <= 35) return "good";
  if (foodCostPct <= 45) return "warn";
  return "bad";
}
